import type {
  AdminMemberCodeItem,
  AdminReferralItem,
  MemberCodeResponse,
  MemberReferralStatus,
  MemberRewardsOverview,
} from '@yoyoclub/shared';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL;

/** Member rewards (docs/prd-member-rewards.md). */

export class MemberRewardsApiError extends Error {
  constructor(
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'MemberRewardsApiError';
  }
}

async function authHeaders(): Promise<HeadersInit> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) {
    throw new MemberRewardsApiError(
      body?.error?.code || (res.status === 429 ? 'RATE_LIMITED' : 'REQUEST_FAILED'),
      body?.error?.message || `Request failed (${res.status})`
    );
  }
  return body.data as T;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export const memberRewardsApi = {
  async overview(): Promise<MemberRewardsOverview> {
    const res = await fetch(`${API_URL}/member-rewards`, { headers: await authHeaders() });
    return unwrap<MemberRewardsOverview>(res);
  },

  /** The member's code, or null when none was generated yet. Never creates one. */
  async getCode(): Promise<MemberCodeResponse | null> {
    const res = await fetch(`${API_URL}/member-rewards/code`, { headers: await authHeaders() });
    return unwrap<MemberCodeResponse | null>(res);
  },

  /** May the signed-in account still use a new user promo code? */
  async eligibility(): Promise<boolean> {
    const res = await fetch(`${API_URL}/member-rewards/eligibility`, {
      headers: await authHeaders(),
    });
    return (await unwrap<{ eligible: boolean }>(res)).eligible;
  },

  /** Creates the member's code on first call, then returns the same one. */
  async createCode(): Promise<MemberCodeResponse> {
    const res = await fetch(`${API_URL}/member-rewards/code`, {
      method: 'POST',
      headers: await authHeaders(),
    });
    return unwrap<MemberCodeResponse>(res);
  },

  /** Invite emails are always in English (the friend's language is unknown). */
  async invite(email: string): Promise<void> {
    const res = await fetch(`${API_URL}/member-rewards/invites`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ email }),
    });
    await unwrap<unknown>(res);
  },

  /** Token-authenticated: the invite email's unsubscribe page. */
  async unsubscribeInvites(token: string): Promise<void> {
    const res = await fetch(`${API_URL}/public/member-invites/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    await unwrap<unknown>(res);
  },

  // --- Admin ---

  async adminReferrals(params: {
    status?: MemberReferralStatus | '';
    search?: string;
    page?: number;
  }): Promise<Paged<AdminReferralItem>> {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.search) q.set('search', params.search);
    q.set('page', String(params.page ?? 1));
    const res = await fetch(`${API_URL}/admin/member-rewards/referrals?${q}`, {
      headers: await authHeaders(),
    });
    return unwrap<Paged<AdminReferralItem>>(res);
  },

  async adminCodes(params: {
    search?: string;
    page?: number;
  }): Promise<Paged<AdminMemberCodeItem>> {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    q.set('page', String(params.page ?? 1));
    const res = await fetch(`${API_URL}/admin/member-rewards/codes?${q}`, {
      headers: await authHeaders(),
    });
    return unwrap<Paged<AdminMemberCodeItem>>(res);
  },

  async adminSetCodeActive(id: string, isActive: boolean): Promise<void> {
    const res = await fetch(`${API_URL}/admin/member-rewards/codes/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: await authHeaders(),
      body: JSON.stringify({ isActive }),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body?.success) {
      throw new MemberRewardsApiError(
        body?.error?.code || 'REQUEST_FAILED',
        body?.error?.message || `Request failed (${res.status})`
      );
    }
  },
};
